import axios from "axios";
import instance from "./ApiConfig";
import store from "../../global/config/store";

/**
 * Structured error thrown by push API calls. Unlike baseApiCall (which only throws the message),
 * the push screens need the HTTP status (409 audience drift, 422 field errors, 503 kill switch).
 */
export interface PushApiError {
  status?: number;
  message: string;
  errors?: { field?: string; message: string }[];
  data?: any;
}

export interface PushRequestOptions {
  /** Skip the global full-screen loader (polling, debounced search, previews). */
  silent?: boolean;
  /** Resolve with the whole axios response (needed for blob export headers). */
  raw?: boolean;
}

const toPushError = async (e: any): Promise<PushApiError> => {
  const res = e?.response;
  let body = res?.data;
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    try {
      body = JSON.parse(await body.text());
    } catch {
      body = undefined;
    }
  }
  const raw = body?.message;
  const msgs: string[] = Array.isArray(raw) ? raw.map(String) : raw ? [String(raw)] : [];
  return {
    status: res?.status,
    message:
      msgs.join(", ") ||
      (res ? "An error occurred while making the request." : "Network error. Please try again."),
    errors: Array.isArray(body?.errors) ? body.errors : undefined,
    data: body?.data,
  };
};

export const pushApiCall = async (config: any, options: PushRequestOptions = {}) => {
  try {
    let response;
    if (options.silent) {
      const token = store.getState().auth.token;
      response = await axios.request({
        ...config,
        headers: {
          ...(config.data ? { "Content-Type": "application/json" } : {}),
          ...(config.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } else {
      response = await instance(config);
    }
    return options.raw ? response : response?.data;
  } catch (e: any) {
    const err = await toPushError(e);
    throw err;
  }
};
